import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  addRemoteFilterToPaidJobs,
  mapFilterValues,
  mapPaidJobFilters,
  WORK_FROM_HOME,
} from 'components/zpFilter/filterUtils';
import { getPaidJobs } from 'store/jobs/actions';
import { JOB_BUCKET_GROUP } from 'utils';
import { PAID_JOBS_CATEGORY } from 'utils/constant';

export const useFallbackToCompanyPaidJobs = (
  props,
  pageSize = 20,
  jobsPerPage = 10,
  isCPP = true,
  jobGroup = JOB_BUCKET_GROUP.CPP_COMPANY_JOBS_BUCKET,
  fallbackJobGroup = JOB_BUCKET_GROUP.CPP_FALLBACK_TO_PAID_JOBS,
  paidJobGroup = JOB_BUCKET_GROUP.PAID_JOBS
) => {
  const { data, useSearchAPI } = props;

  const dispatch = useDispatch();

  const { companyInfo, remoteJobs, isVisaSponsorshipSet, jobTitle } = data;

  const {
    companyID,
    newCategories = [],
    companyCategories = [],
    companySimilarCompanies = [],
  } = companyInfo;

  const categories = companyCategories.length
    ? companyCategories.map((cat) => cat.text || cat).slice(0, 3)
    : newCategories;

  const filterQueue = useSelector(({ searchFilter }) => searchFilter.stateHistory[jobGroup]);

  const companyJobBucket = useSelector(({ jobs }) => jobs[jobGroup] || {});

  const paidJobBucket = useSelector(({ jobs }) => jobs[paidJobGroup] || {});

  const companyFallbackJobBucket = useSelector(({ jobs }) => jobs[fallbackJobGroup] || {});

  const cppFallbackToPaidJobs = useSelector(
    ({ locals }) => locals.displayPaidJobs.cppFallbackToPaidJobs
  );

  const hideFallbackJobBucket = useMemo(
    () =>
      !!(
        companyFallbackJobBucket.hideOnEmpty &&
        companyFallbackJobBucket.totalJobs === 0 &&
        companyFallbackJobBucket.loading === false
      ),
    [companyFallbackJobBucket]
  );

  const createPaidJobsAPIPayload = useCallback(
    (numJobs = 4) => ({
      primaryCompanyID: companyID,
      category: categories.length
        ? PAID_JOBS_CATEGORY.COMPANY_CATEGORIES
        : PAID_JOBS_CATEGORY.COMPANY_CLUSTER,
      ...(categories.length
        ? { companyCategories: categories }
        : { companyIDs: companySimilarCompanies.map((company) => company.companyID) }),
      numJobs,
    }),
    [companyID, categories, companySimilarCompanies]
  );

  const createSectionTitle = useCallback(() => {
    const length = categories.length;
    if (length === 0) {
      return 'Jobs In Similar Companies';
    }
    const lastIndex = length - 1;
    const categoryText =
      length > 1
        ? categories.slice(0, lastIndex).join(',') + ` and ${categories[lastIndex]}`
        : categories[0];
    return `Jobs In ${categoryText}`;
  }, [categories]);

  const noCompanyJobs = useMemo(
    () => !!(companyJobBucket?.jobs?.length === 0 && cppFallbackToPaidJobs),
    [companyJobBucket, cppFallbackToPaidJobs]
  );

  const noPaidJobs = useMemo(
    () =>
      !!(
        paidJobBucket.hideOnEmpty &&
        paidJobBucket.totalJobs === 0 &&
        paidJobBucket.loading === false &&
        cppFallbackToPaidJobs
      ),
    [paidJobBucket, cppFallbackToPaidJobs]
  );

  const initialPayload = useMemo(
    () => ({
      [useSearchAPI ? 'companyIDs' : 'companyID']: [companyID],
      ...(isVisaSponsorshipSet && { visaSponsorship: 'might_sponsor' }),
      ...(remoteJobs ? { remoteCategories: [WORK_FROM_HOME] } : {}),
      ...(jobTitle
        ? { [useSearchAPI ? 'titles' : 'title']: useSearchAPI ? [jobTitle] : jobTitle }
        : {}),
    }),
    [useSearchAPI, companyID, isVisaSponsorshipSet, remoteJobs, jobTitle]
  );

  const title = useMemo(
    () =>
      noCompanyJobs && !companyJobBucket?.loading
        ? createSectionTitle()
        : isCPP
        ? `${companyInfo.companyNameCPS} Jobs And Careers`
        : `${companyInfo.companyNameCPS} Jobs`,
    [noCompanyJobs, companyInfo, isCPP, createSectionTitle, companyJobBucket?.loading]
  );

  const paidJobAPIPayload = useMemo(
    () => ({
      ...createPaidJobsAPIPayload(4),
      ...(jobTitle ? { title: jobTitle } : {}),
    }),
    [createPaidJobsAPIPayload, jobTitle]
  );

  useEffect(() => {
    let filterDetail = {};
    if (filterQueue) {
      const filterQueueValues = filterQueue[filterQueue.length - 1];
      filterDetail = mapFilterValues(filterQueueValues, initialPayload);
    }
    const dispatchPaidJobs = (payload) =>
      dispatch(
        getPaidJobs({
          key: JOB_BUCKET_GROUP.PAID_JOBS,
          detail: {
            ...payload,
            ...mapPaidJobFilters(filterDetail, payload),
            ...addRemoteFilterToPaidJobs(filterQueue),
          },
        })
      );
    if (
      !companyJobBucket.loading &&
      companyJobBucket.pageNo > -1 &&
      companyJobBucket?.jobs?.length === 0 &&
      !paidJobBucket.loading &&
      paidJobBucket?.jobs?.length === 0 &&
      paidJobBucket.totalJobs !== 0
    ) {
      const payload = createPaidJobsAPIPayload(jobsPerPage);
      dispatchPaidJobs(payload);
    }
  }, [
    companyJobBucket,
    noPaidJobs,
    createPaidJobsAPIPayload,
    dispatch,
    filterQueue,
    initialPayload,
    jobsPerPage,
    paidJobBucket?.jobs?.length,
    paidJobBucket.loading,
    paidJobBucket.totalJobs,
  ]);

  const mainBucketProps = useMemo(
    () => ({
      title,
      initialPayload,
      jobsPerPage: pageSize,
      enablePaidFixedPos: !useSearchAPI,
      hideFallbackJobBucket: !!useSearchAPI,
      paidJobAPIPayload: useSearchAPI ? {} : paidJobAPIPayload,
      jobGroup: useSearchAPI ? JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API : jobGroup,
      filterGroup: useSearchAPI ? JOB_BUCKET_GROUP.JOBS_FROM_SEACH_API : jobGroup,
    }),
    [
      useSearchAPI,
      title,
      hideFallbackJobBucket,
      initialPayload,
      jobGroup,
      pageSize,
      paidJobAPIPayload,
    ]
  );

  return mainBucketProps;
};

export const withFallbackToCompanyPaidJobs = (
  CompanyClusterPage,
  pageSize = 20,
  jobsPerPage = 10,
  isCPP = true,
  jobGroup = JOB_BUCKET_GROUP.CPP_COMPANY_JOBS_BUCKET,
  fallbackJobGroup = JOB_BUCKET_GROUP.CPP_FALLBACK_TO_PAID_JOBS
) => {
  const Component = (props) => {
    const mainBucketProps = useFallbackToCompanyPaidJobs(
      props,
      pageSize,
      jobsPerPage,
      isCPP,
      jobGroup,
      fallbackJobGroup
    );

    return <CompanyClusterPage {...props} mainBucketProps={mainBucketProps} />;
  };

  Component.getInitialProps = CompanyClusterPage.getInitialProps;

  return Component;
};
